import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, FileText, Wallet, CreditCard, CheckCircle2, XCircle, AlertTriangle, ExternalLink } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { InvoiceStatusBadge, PaymentStatusBadge, AccountStatusBadge, formatIDR } from "./BillingBadges";

function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString("id-ID"); } catch { return iso; } }

function Row({ label, value, mono = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-white/5 last:border-0">
      <div className="text-[10px] text-white/50 uppercase tracking-wider">{label}</div>
      <div className={`text-xs text-white text-right min-w-0 truncate ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

export default function BillingDetail() {
  const { tenantId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/platform/billing/tenant/${tenantId}`);
      setData(r.data);
    } catch (e) { notifyError(e); setData(null); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (id) => {
    try {
      await api.post(`/platform/billing/invoice/${id}/paid`, { note: "" });
      toast.success("Invoice ditandai PAID.");
      load();
    } catch (e) { notifyError(e); }
  };
  const voidInvoice = async (id) => {
    const reason = window.prompt("Alasan void invoice?");
    if (!reason || reason.length < 3) return;
    try {
      await api.post(`/platform/billing/invoice/${id}/void`, { reason });
      toast.success("Invoice di-void.");
      load();
    } catch (e) { notifyError(e); }
  };

  if (loading) return (
    <PlatformLayout title="Billing Detail"><div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center" data-testid="bdt-loading">Memuat…</div></PlatformLayout>
  );

  if (!data) return (
    <PlatformLayout title="Billing Detail">
      <div className="p-8 rounded-2xl border border-white/10 text-center space-y-3" data-testid="bdt-not-found">
        <div className="text-white/70 text-sm">Tenant tidak ditemukan.</div>
        <Button variant="ghost" onClick={() => nav("/platform/billing")} className="text-white/80 hover:text-white hover:bg-white/10" data-testid="bdt-not-found-back">
          <ArrowLeft size={14} className="mr-1" /> Kembali
        </Button>
      </div>
    </PlatformLayout>
  );

  const acc = data.account || {};
  const invoices = data.invoices || [];
  const payments = data.payments || [];

  return (
    <PlatformLayout title={data.tenant_name || "Billing Detail"}>
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Button variant="ghost" onClick={() => nav("/platform/billing")} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="bdt-back">
          <ArrowLeft size={14} className="mr-1" /> Kembali
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" onClick={load} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="bdt-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Link to={`/platform/monitoring/${tenantId}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-xs font-bold" data-testid="bdt-open-monitoring">
            <ExternalLink size={12} /> Monitoring
          </Link>
        </div>
      </div>

      {/* Overview */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5 mb-5 flex flex-wrap items-center gap-4" data-testid="bdt-overview">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-white font-display font-black text-lg truncate" data-testid="bdt-tenant-name">{data.tenant_name}</div>
            {acc.status && <AccountStatusBadge status={acc.status} />}
            <span className="text-[10px] text-white/40 font-mono">· {data.tenant_id}</span>
          </div>
          <div className="text-xs text-white/60 font-mono truncate">{acc.plan_code} · {acc.billing_cycle}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Outstanding</div>
          <div className="text-xl font-black font-display text-amber-300" data-testid="bdt-outstanding">{formatIDR(data.outstanding)}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Total Paid</div>
          <div className="text-xl font-black font-display text-emerald-300" data-testid="bdt-total-paid">{formatIDR(data.total_paid)}</div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="bdt-section-account">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard size={14} className="text-emerald-300" />
            <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Billing Account</div>
          </div>
          <Row label="Status" value={acc.status} />
          <Row label="Plan" value={acc.plan_code} mono />
          <Row label="Billing Cycle" value={acc.billing_cycle} mono />
          <Row label="Currency" value={acc.currency} mono />
          <Row label="Last Billing" value={fmt(acc.last_billing_date)} />
          <Row label="Next Billing" value={fmt(acc.next_billing_date)} />
          <Row label="Grace Started" value={fmt(acc.grace_started_at)} />
          <Row label="Suspended At" value={fmt(acc.suspended_at)} />
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="bdt-section-outstanding">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={14} className="text-amber-300" />
            <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Outstanding Invoices</div>
          </div>
          {invoices.filter((i) => ["PENDING", "UNPAID", "OVERDUE"].includes(i.status)).length === 0 ? (
            <div className="text-emerald-300/70 text-xs italic py-2" data-testid="bdt-no-outstanding">Tidak ada tagihan outstanding.</div>
          ) : invoices.filter((i) => ["PENDING", "UNPAID", "OVERDUE"].includes(i.status)).map((i) => (
            <div key={i.invoice_id} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0" data-testid={`bdt-outstanding-${i.invoice_id}`}>
              <span className="text-xs font-mono text-white truncate">{i.invoice_number}</span>
              <InvoiceStatusBadge status={i.status} />
              <span className="ml-auto text-xs font-bold text-white">{formatIDR(i.grand_total)}</span>
              <button onClick={() => markPaid(i.invoice_id)} className="w-6 h-6 rounded-lg grid place-items-center text-emerald-300 hover:bg-emerald-500/20" title="Mark Paid" data-testid={`bdt-inv-paid-${i.invoice_id}`}>
                <CheckCircle2 size={11} />
              </button>
              <button onClick={() => voidInvoice(i.invoice_id)} className="w-6 h-6 rounded-lg grid place-items-center text-rose-300 hover:bg-rose-500/20" title="Void" data-testid={`bdt-inv-void-${i.invoice_id}`}>
                <XCircle size={11} />
              </button>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 md:col-span-2" data-testid="bdt-section-invoices">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={14} className="text-sky-300" />
            <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Invoice History ({invoices.length})</div>
          </div>
          {invoices.length === 0 ? (
            <div className="text-white/40 text-xs italic py-2" data-testid="bdt-invoices-empty">Belum ada invoice.</div>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
              {invoices.map((i) => (
                <div key={i.invoice_id} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0 text-xs" data-testid={`bdt-inv-${i.invoice_id}`}>
                  <span className="font-mono text-white truncate">{i.invoice_number}</span>
                  <InvoiceStatusBadge status={i.status} />
                  <span className="text-[10px] text-white/40 ml-2">{fmt(i.issue_date)}</span>
                  <span className="ml-auto font-bold text-white">{formatIDR(i.grand_total)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 md:col-span-2" data-testid="bdt-section-payments">
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={14} className="text-indigo-300" />
            <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Payment History ({payments.length})</div>
          </div>
          {payments.length === 0 ? (
            <div className="text-white/40 text-xs italic py-2" data-testid="bdt-payments-empty">Belum ada pembayaran.</div>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
              {payments.map((p) => (
                <div key={p.payment_id} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0 text-xs" data-testid={`bdt-pay-${p.payment_id}`}>
                  <span className="font-mono text-white truncate">{p.invoice_number}</span>
                  <PaymentStatusBadge status={p.status} />
                  <span className="text-[10px] text-white/40 font-mono ml-2">{p.method}</span>
                  <span className="text-[10px] text-white/40 ml-2">{fmt(p.transaction_time || p.created_at)}</span>
                  <span className="ml-auto font-bold text-white">{formatIDR(p.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PlatformLayout>
  );
}
